import { useEffect, useState } from 'react'
import { useImagePixels } from './useImagePixels'
import { Caption, COLORS, Panel, Badge } from './ui'

const G = 20

// Zoom from image → pixel grid → the RGB triple behind one pixel.
export function Pixels2D() {
  const pixels = useImagePixels('/cv/ref-cat.png', G, G)
  const [sel, setSel] = useState(Math.floor((G * G) / 2 + G / 2))
  const pix = pixels ? pixels[sel] : [231, 201, 184]
  const [r, g, b] = pix
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const imgSize = mobile ? 170 : 200
  const gridSize = mobile ? 190 : 220

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: mobile ? 6 : 12, padding: mobile ? '80px 20px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: mobile ? 10 : 26, flexWrap: 'wrap', width: mobile ? '100%' : undefined }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: mobile ? 4 : 8 }}>
          <Badge>Original · H × W × 3</Badge>
          <img src="/cv/ref-cat.png" alt="cat" style={{ width: imgSize, height: imgSize, objectFit: 'cover', borderRadius: 10 }} />
        </div>

        <div style={{ color: COLORS.indigo, fontSize: mobile ? 16 : 24, opacity: 0.6, transform: mobile ? 'rotate(90deg)' : 'none' }}>→</div>

        {/* Pixel grid — hover/click to inspect (pointer-events enabled just on the grid) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: mobile ? 4 : 8 }}>
          <Badge color={COLORS.purple}>Pixel grid · {G}×{G}</Badge>
          <div style={{
            display: 'grid', gridTemplateColumns: `repeat(${G}, 1fr)`, gap: 1,
            width: gridSize, height: gridSize, pointerEvents: 'auto', cursor: 'crosshair',
          }}>
            {(pixels || []).map(([pr, pg, pb], i) => (
              <div
                key={i}
                onMouseEnter={() => setSel(i)}
                style={{
                  background: `rgb(${pr},${pg},${pb})`,
                  outline: i === sel ? `2px solid ${COLORS.purple}` : 'none',
                  outlineOffset: -1, zIndex: i === sel ? 2 : 1,
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ color: COLORS.indigo, fontSize: mobile ? 16 : 24, opacity: 0.6, transform: mobile ? 'rotate(90deg)' : 'none' }}>→</div>

        {/* RGB readout */}
        <Panel style={{ width: mobile ? '85%' : 190, maxWidth: mobile ? 260 : undefined, padding: mobile ? 10 : 16, display: 'flex', flexDirection: 'column', gap: mobile ? 6 : 10 }}>
          <div style={{ width: '100%', height: mobile ? 28 : 40, borderRadius: 8, background: `rgb(${r},${g},${b})`, border: '1px solid rgba(255,255,255,0.15)' }} />
          {[['R', r, COLORS.red], ['G', g, COLORS.green], ['B', b, COLORS.blue]].map(([label, val, c]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 16, color: c, fontWeight: 700, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{label}</span>
              <div style={{ flex: 1, height: 7, background: 'rgba(255,255,255,0.08)', borderRadius: 4 }}>
                <div style={{ width: `${(val / 255) * 100}%`, height: '100%', background: c, borderRadius: 4 }} />
              </div>
              <span style={{ width: 28, textAlign: 'right', fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 13 }}>{val}</span>
            </div>
          ))}
        </Panel>
      </div>

      <Caption>
        Each pixel is three numbers — red, green, blue, each 0–255. An image is just a stack of these:
        a height × width × 3 array. Hover the grid to read any pixel.
      </Caption>
    </div>
  )
}
